import React, { useState, useEffect } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { API_URL } from "../../../setup/config/env";
import { getImageSource } from "../api/routes";
import { getContact, getTemplates } from "../api/actions/query";
import { messageShow } from "../../common/api/actions";
import { checkPermission } from "../../common/Sidebar/checkPermission";
import TemplateSelectionPopup from "../List/Messaging";
import UserEmailHistory from "../../common/UserEmailHistory";
import BrandedLoading from "../../common/BrandedLoading";

import {
  Box,
  Card,
  Typography,
  Grid,
  Chip,
  Button,
  IconButton,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Avatar,
  Link as MuiLink,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";

import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Send as SendIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Storage as DbIcon,
  Comment as CommentIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Extension as ExtensionIcon,
  OpenInNew as OpenInNewIcon,
  Description as FileIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";

const BRAND_BLUE = "#0d3559";
const BRAND_ORANGE = "#ea580c";
const BRAND_ORANGE_LIGHT = "#fff7ed";
const BRAND_ORANGE_BORDER = "#fed7aa";

const formatLabel = (text = "") => {
  if (!text) return "";
  return text
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

const isUrl = (string) => {
  if (typeof string !== "string") return false;
  try {
    const parsed = new URL(string);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch (_) {
    return false;
  }
};

const isFileUrl = (url = "") => {
  if (!isUrl(url)) return false;
  const lower = url.toLowerCase();
  return (
    lower.endsWith(".pdf") ||
    lower.endsWith(".doc") ||
    lower.endsWith(".docx") ||
    lower.endsWith(".png") ||
    lower.endsWith(".jpg") ||
    lower.endsWith(".jpeg") ||
    lower.includes("cloudfront") ||
    lower.includes("/files/") ||
    lower.includes("/documents/")
  );
};

const getLeadStatusColor = (status) => {
  switch ((status || "").toLowerCase()) {
    case "customer":
      return { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" };
    case "prospect":
      return { bg: BRAND_ORANGE_LIGHT, color: BRAND_ORANGE, border: BRAND_ORANGE_BORDER };
    case "lead":
      return { bg: "#f0f9ff", color: "#0284c7", border: "#bae6fd" };
    case "contact":
    default:
      return { bg: "#f8fafc", color: BRAND_BLUE, border: "#cbd5e1" };
  }
};

const CRMDetail = () => {
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const dispatch = useDispatch();

  const contactId = params.contactId || params.id;
  const activeTabFromRoute = params.tab || location.state?.activeTab || "Contacts";

  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState(null);
  const [currentTab, setCurrentTab] = useState(0); // 0: Overview, 1: Email History

  // Permissions
  const [allPermissions, setAllPermissions] = useState({
    contactsPermissions: { canView: true, canEdit: true, canDelete: true, canCreate: true },
    leadsPermissions: { canView: true, canEdit: true, canDelete: true, canCreate: true },
    prospectPermissions: { canView: true, canEdit: true, canDelete: true, canCreate: true },
    customerPermissions: { canView: true, canEdit: true, canDelete: true, canCreate: true },
  });

  // Messaging Popup
  const [messagingOpen, setMessagingOpen] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    loadPermissions();
    fetchContactDetails();
    fetchEmailTemplates();
  }, [contactId, location.key]);

  const loadPermissions = async () => {
    try {
      const contactsPermissions = await checkPermission("engagement", "crm.contacts");
      const leadsPermissions = await checkPermission("engagement", "crm.lead");
      const prospectPermissions = await checkPermission("engagement", "crm.prospect");
      const customerPermissions = await checkPermission("engagement", "crm.customer");

      const loaded = {
        contactsPermissions: contactsPermissions || { canView: true, canEdit: true, canDelete: true, canCreate: true },
        leadsPermissions: leadsPermissions || { canView: true, canEdit: true, canDelete: true, canCreate: true },
        prospectPermissions: prospectPermissions || { canView: true, canEdit: true, canDelete: true, canCreate: true },
        customerPermissions: customerPermissions || { canView: true, canEdit: true, canDelete: true, canCreate: true },
      };
      setAllPermissions(loaded);
    } catch (err) {
      console.warn("Failed to load CRM permissions:", err);
    }
  };

  const fetchContactDetails = async () => {
    if (!contactId) return;

    if (!contact && location.state?.contactData && location.state.contactData._id === contactId) {
      setContact(location.state.contactData);
      setLoading(false);
    } else if (!contact) {
      setLoading(true);
    }

    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/crm/${contactId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (res.data && res.data.success && res.data.data) {
        setContact(res.data.data);
      } else if (!contact) {
        dispatch(messageShow(res.data?.message || "Failed to load contact details."));
      }
    } catch (err) {
      console.error("Error fetching CRM contact:", err);
      if (!contact) {
        dispatch(messageShow("Error loading contact details."));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const res = await getTemplates();
      if (res.data && res.data.success) {
        setTemplates((res.data.data || []).filter((t) => !t.isDefault));
      }
    } catch (err) {
      console.error("Error fetching templates:", err);
    }
  };

  const handleBack = () => {
    history.push("/crm", { activeTab: activeTabFromRoute });
  };

  const getEditRouteInfo = () => {
    const raw = (activeTabFromRoute || contact?.leadStatus || "Contacts").toLowerCase();
    if (raw.startsWith("lead")) {
      return { path: `/crm/edit/leads/${contactId}`, tab: "Leads" };
    }
    if (raw.startsWith("prospect")) {
      return { path: `/crm/edit/prospect/${contactId}`, tab: "Prospect" };
    }
    if (raw.startsWith("customer")) {
      return { path: `/crm/edit/customer/${contactId}`, tab: "Customer" };
    }
    return { path: `/crm/edit/contacts/${contactId}`, tab: "Contacts" };
  };

  const handleEdit = () => {
    const { path, tab } = getEditRouteInfo();
    history.push(path, {
      contactData: contact,
      activeTab: tab,
      permissions: allPermissions,
      availableTags: contact?.tags || [],
    });
  };

  const getCommentText = (comments) => {
    if (!comments) return "";
    if (Array.isArray(comments)) {
      if (comments.length === 0) return "";
      return comments
        .map((c) => (typeof c === "string" ? c : c.comment || c.text || JSON.stringify(c)))
        .filter(Boolean)
        .join("\n");
    }
    if (typeof comments === "string") return comments.trim();
    return String(comments).trim();
  };

  // Helper to render any arbitrary extension field value cleanly
  const renderValueDisplay = (val) => {
    if (val === null || val === undefined || val === "") {
      return <Typography variant="body1" sx={{ color: "#94a3b8", fontSize: "14px" }}>-</Typography>;
    }

    if (typeof val === "boolean") {
      return (
        <Chip
          label={val ? "Yes" : "No"}
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: "11px",
            height: 22,
            bgcolor: val ? "#f0fdf4" : "#fef2f2",
            color: val ? "#16a34a" : "#dc2626",
            border: `1px solid ${val ? "#bbf7d0" : "#fecaca"}`,
          }}
        />
      );
    }

    if (typeof val === "string" && isFileUrl(val)) {
      const fileName = val.split("/").pop() || "Document";
      return (
        <MuiLink
          href={val}
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.8,
            px: 1.5,
            py: 0.6,
            bgcolor: "#f8fafc",
            border: "1px solid #cbd5e1",
            borderRadius: "6px",
            color: BRAND_BLUE,
            fontWeight: 600,
            fontSize: "13px",
            "&:hover": { bgcolor: "rgba(13,53,89,0.06)", borderColor: BRAND_BLUE },
          }}
        >
          <FileIcon sx={{ fontSize: 16, color: BRAND_ORANGE }} />
          <span style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {fileName}
          </span>
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </MuiLink>
      );
    }

    if (typeof val === "string" && isUrl(val)) {
      return (
        <MuiLink
          href={val}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: BRAND_BLUE,
            fontSize: "13.5px",
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            wordBreak: "break-all",
          }}
        >
          {val}
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </MuiLink>
      );
    }

    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val)) {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        return (
          <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "14px" }}>
            {dayjs(val).format("DD MMM YYYY, hh:mm A")}
          </Typography>
        );
      }
    }

    if (Array.isArray(val)) {
      if (val.length === 0) return <Typography variant="body1" sx={{ color: "#94a3b8", fontSize: "14px" }}>-</Typography>;

      // Array of simple strings/numbers
      const isSimpleArray = val.every((item) => typeof item !== "object" || item === null);
      if (isSimpleArray) {
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
            {val.map((item, idx) => {
              const itemStr = String(item || "");
              if (isFileUrl(itemStr)) {
                return (
                  <Box key={idx} sx={{ mr: 0.5, mb: 0.5 }}>
                    {renderValueDisplay(itemStr)}
                  </Box>
                );
              }
              if (isUrl(itemStr)) {
                return (
                  <MuiLink
                    key={idx}
                    href={itemStr}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: BRAND_BLUE,
                      fontSize: "13px",
                      fontWeight: 600,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      bgcolor: "#f1f5f9",
                      px: 1,
                      py: 0.4,
                      borderRadius: "4px",
                    }}
                  >
                    {itemStr}
                    <OpenInNewIcon sx={{ fontSize: 13 }} />
                  </MuiLink>
                );
              }
              return (
                <Chip
                  key={idx}
                  label={itemStr}
                  size="small"
                  sx={{ bgcolor: "#f1f5f9", color: "#334155", fontWeight: 600, fontSize: "12px", height: 24 }}
                />
              );
            })}
          </Box>
        );
      }

      // Array of objects
      if (val.length > 1) {
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%", mt: 1 }}>
            {val.map((subObj, idx) => (
              <Accordion
                key={idx}
                defaultExpanded={false}
                disableGutters
                elevation={0}
                sx={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px !important",
                  bgcolor: "#f8fafc",
                  "&:before": { display: "none" },
                  overflow: "hidden",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: BRAND_BLUE }} />}
                  sx={{
                    minHeight: 40,
                    px: 1.5,
                    "& .MuiAccordionSummary-content": { my: 0.6, display: "flex", alignItems: "center", gap: 1 },
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "12.5px" }}>
                    Entry #{idx + 1}
                  </Typography>
                  {subObj?.title || subObj?.name || subObj?.status || subObj?.type ? (
                    <Chip
                      label={String(subObj.title || subObj.name || subObj.status || subObj.type)}
                      size="small"
                      sx={{ height: 20, fontSize: "11px", bgcolor: "#e2e8f0", color: "#334155" }}
                    />
                  ) : null}
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2, pt: 1, bgcolor: "#ffffff", borderTop: "1px solid #e2e8f0" }}>
                  <Grid container spacing={1.5}>
                    {Object.keys(subObj || {}).map((k) => (
                      <Grid item xs={12} sm={6} key={k}>
                        <Typography variant="caption" sx={{ color: "#64748b", fontSize: "11.5px", display: "block" }}>
                          {formatLabel(k)}
                        </Typography>
                        {renderValueDisplay(subObj[k])}
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        );
      }

      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%", mt: 1 }}>
          {val.map((subObj, idx) => (
            <Box
              key={idx}
              sx={{
                p: 1.5,
                bgcolor: "#f8fafc",
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "12px", display: "block", mb: 1 }}>
                Entry #{idx + 1}
              </Typography>
              <Grid container spacing={1.5}>
                {Object.keys(subObj || {}).map((k) => (
                  <Grid item xs={12} sm={6} key={k}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontSize: "11.5px", display: "block" }}>
                      {formatLabel(k)}
                    </Typography>
                    {renderValueDisplay(subObj[k])}
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      );
    }

    if (typeof val === "object") {
      return (
        <Box sx={{ p: 1.5, bgcolor: "#f8fafc", borderRadius: "6px", border: "1px solid #e2e8f0", width: "100%", mt: 0.5 }}>
          <Grid container spacing={1.5}>
            {Object.keys(val).map((k) => (
              <Grid item xs={12} sm={6} key={k}>
                <Typography variant="caption" sx={{ color: "#64748b", fontSize: "11.5px", display: "block" }}>
                  {formatLabel(k)}
                </Typography>
                {renderValueDisplay(val[k])}
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    }

    return (
      <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "14px", wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}>
        {String(val)}
      </Typography>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, py: 6 }}>
        <BrandedLoading />
      </Box>
    );
  }

  if (!contact) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          Contact not found
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mt: 2, textTransform: "none", bgcolor: BRAND_BLUE, "&:hover": { bgcolor: "#08223a" } }}
        >
          Back to CRM
        </Button>
      </Box>
    );
  }

  const statusStyle = getLeadStatusColor(contact.leadStatus);
  const displayName = contact.storeName || contact.contactName || "Unnamed Contact";
  const avatarImage = contact.image ? getImageSource(contact.image) : null;
  const commentText = getCommentText(contact.comments);
  const hasComments = Boolean(commentText && commentText.length > 0);

  // Filter out internal metadata from extensions
  const extensions = contact.extensions || {};
  const validExtensionKeys = Object.keys(extensions).filter((k) => {
    const lower = k.toLowerCase();
    return lower !== "timezone" && !lower.includes("submissionhistory") && extensions[k] !== undefined && extensions[k] !== null;
  });

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, maxWidth: 1200, mx: "auto" }}>
      {/* Top App Header & Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2.5,
          pb: 1.5,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            color="inherit"
            onClick={handleBack}
            edge="start"
            sx={{
              mr: 1,
              color: BRAND_BLUE,
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: { xs: "17px", sm: "19px" }, lineHeight: 1.2 }}>
              {displayName}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", fontSize: "12px", mt: 0.2 }}>
              CRM Profile • {contact.createdDate ? `Added on ${dayjs(contact.createdDate).format("DD MMM YYYY")}` : "Direct Contact"}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {(() => {
            const raw = (activeTabFromRoute || contact?.leadStatus || "Contacts").toLowerCase();
            let currentPerm = allPermissions.contactsPermissions;
            if (raw.startsWith("lead")) currentPerm = allPermissions.leadsPermissions;
            else if (raw.startsWith("prospect")) currentPerm = allPermissions.prospectPermissions;
            else if (raw.startsWith("customer")) currentPerm = allPermissions.customerPermissions;
            const canEdit = currentPerm?.canEdit ?? true;

            return (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SendIcon sx={{ fontSize: 15 }} />}
                  onClick={() => setMessagingOpen(true)}
                  disabled={!canEdit}
                  sx={{
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "12.5px",
                    borderColor: BRAND_ORANGE,
                    color: BRAND_ORANGE,
                    py: "4px",
                    px: 1.8,
                    "&:hover": { bgcolor: BRAND_ORANGE_LIGHT, borderColor: BRAND_ORANGE },
                    "&.Mui-disabled": { borderColor: "#fed7aa", color: "#fed7aa" },
                  }}
                >
                  Send Email
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                  onClick={handleEdit}
                  disabled={!canEdit}
                  sx={{
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "12.5px",
                    bgcolor: BRAND_BLUE,
                    py: "4px",
                    px: 2,
                    "&:hover": { bgcolor: "#08223a" },
                  }}
                >
                  Edit
                </Button>
              </>
            );
          })()}
        </Box>
      </Box>

      {/* Main Profile Summary Card */}
      <Card
        variant="outlined"
        sx={{
          borderRadius: "8px",
          mb: 2.5,
          p: 2.5,
          bgcolor: "#ffffff",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
        }}
      >
        <Grid container spacing={2.5} alignItems="center">
          <Grid item>
            {avatarImage ? (
              <Avatar
                src={avatarImage}
                alt={displayName}
                sx={{ width: 64, height: 64, borderRadius: "8px", border: "1px solid #e2e8f0" }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "8px",
                  bgcolor: BRAND_BLUE,
                  fontSize: 22,
                  fontWeight: 700,
                }}
              >
                {displayName.charAt(0).toUpperCase()}
              </Avatar>
            )}
          </Grid>

          <Grid item xs={12} sm sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.2, mb: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "17px" }}>
                {displayName}
              </Typography>
              {contact.leadStatus && (
                <Chip
                  label={contact.leadStatus}
                  size="small"
                  sx={{
                    bgcolor: statusStyle.bg,
                    color: statusStyle.color,
                    border: `1px solid ${statusStyle.border}`,
                    fontWeight: 700,
                    fontSize: "12px",
                    height: 24,
                  }}
                />
              )}
              {contact.businessType && (
                <Chip
                  label={contact.businessType}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: "12px", height: 24, borderColor: "#cbd5e1", color: "#475569" }}
                />
              )}
              {contact.subscribedPlan && (
                <Chip
                  label={`Plan: ${contact.subscribedPlan}`}
                  size="small"
                  sx={{ bgcolor: BRAND_ORANGE_LIGHT, color: BRAND_ORANGE, border: `1px solid ${BRAND_ORANGE_BORDER}`, fontWeight: 700, fontSize: "12px", height: 24 }}
                />
              )}
            </Box>

            {/* Quick Contact Info Row */}
            <Grid container spacing={2}>
              {contact.contactName && contact.storeName && contact.storeName.trim().toLowerCase() !== contact.contactName.trim().toLowerCase() && (
                <Grid item xs={12} sm={6} md={3} sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <PersonIcon sx={{ fontSize: 17, color: BRAND_BLUE, flexShrink: 0 }} />
                  <Typography variant="body1" sx={{ color: "#334155", fontSize: "13.5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }} title={contact.contactName}>
                    {contact.contactName}
                  </Typography>
                </Grid>
              )}
              {contact.email && (
                <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <EmailIcon sx={{ fontSize: 17, color: BRAND_BLUE, flexShrink: 0 }} />
                  <Typography variant="body1" sx={{ color: "#334155", fontSize: "13.5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }} title={contact.email}>
                    {contact.email}
                  </Typography>
                </Grid>
              )}
              {contact.phoneNumber && (
                <Grid item xs={12} sm={6} md={3} sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <PhoneIcon sx={{ fontSize: 17, color: BRAND_BLUE, flexShrink: 0 }} />
                  <Typography variant="body1" sx={{ color: "#334155", fontSize: "13.5px", minWidth: 0, whiteSpace: "nowrap" }}>
                    {contact.phoneNumber}
                  </Typography>
                </Grid>
              )}
              {contact.region && (
                <Grid item xs={12} sm={6} md={2} sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <LocationIcon sx={{ fontSize: 17, color: BRAND_BLUE, flexShrink: 0 }} />
                  <Typography variant="body1" sx={{ color: "#334155", fontSize: "13.5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }} title={`${contact.region} ${contact.pincode ? `(${contact.pincode})` : ""}`}>
                    {contact.region} {contact.pincode ? `(${contact.pincode})` : ""}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Card>

      {/* Modern Tabs Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: "#e2e8f0", mb: 2.5 }}>
        <Tabs
          value={currentTab}
          onChange={(e, val) => setCurrentTab(val)}
          sx={{
            minHeight: 44,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 700,
              fontSize: "14px",
              minHeight: 44,
              py: 0.8,
              color: "#64748b",
              "&.Mui-selected": { color: BRAND_BLUE },
            },
            "& .MuiTabs-indicator": {
              bgcolor: BRAND_BLUE,
              height: 3,
              borderRadius: "2px 2px 0 0",
            },
          }}
        >
          <Tab icon={<PersonIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Overview Details" />
          <Tab icon={<HistoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Email & Workflow History" />
        </Tabs>
      </Box>

      {/* TAB 0: OVERVIEW */}
      {currentTab === 0 && (
        <Grid container spacing={2.5}>
          {/* Card 1: Contact & Store Information */}
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: "8px",
                p: 2.5,
                border: "1px solid #e2e8f0",
                bgcolor: "#ffffff",
                height: "100%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2, pb: 1.2, borderBottom: "1px solid #f1f5f9" }}>
                <BusinessIcon sx={{ fontSize: 20, color: BRAND_BLUE }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "15px" }}>
                  Contact & Business Details
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                    Store Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "14px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {contact.storeName || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                    Contact Person Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "14px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {contact.contactName || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "14px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {contact.email || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                    Phone Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "14px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {contact.phoneNumber || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                    Business Size
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "14px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {contact.businessType || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                    Growth Stage
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "14px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {contact.storeGrowthStage || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                    Subscribed Plan
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: BRAND_ORANGE, fontSize: "14px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {contact.subscribedPlan || "None"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                    Lead Status
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: statusStyle.color, fontSize: "14px", textTransform: "capitalize", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {contact.leadStatus || "Contact"}
                  </Typography>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* Card 2: Location, Activity & Notes */}
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: "8px",
                p: 2.5,
                border: "1px solid #e2e8f0",
                bgcolor: "#ffffff",
                height: "100%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2, pb: 1.2, borderBottom: "1px solid #f1f5f9" }}>
                <LocationIcon sx={{ fontSize: 20, color: BRAND_BLUE }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "15px" }}>
                  Location & Notes
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                    Region
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "14px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {contact.region || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                    Pin Code
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "14px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {contact.pincode || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                    Address
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: "#334155", fontSize: "13.5px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {contact.address || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                    Created Date
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: "#334155", fontSize: "13.5px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {contact.createdDate ? dayjs(contact.createdDate).format("DD MMM YYYY, hh:mm A") : "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                    Last Updated
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: "#334155", fontSize: "13.5px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {contact.updatedDate ? dayjs(contact.updatedDate).format("DD MMM YYYY, hh:mm A") : "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                    Note
                  </Typography>
                  <Box sx={{ p: 1.5, bgcolor: "#f8fafc", borderRadius: "6px", border: "1px solid #e2e8f0", mt: 0.5, minWidth: 0 }}>
                    {hasComments ? (
                      <Typography variant="body1" sx={{ color: "#334155", fontSize: "13.5px", whiteSpace: "pre-wrap", lineHeight: 1.5, wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {commentText}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ color: "#94a3b8", fontSize: "13.5px", fontStyle: "italic" }}>
                        No comments or notes added
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* Card: Inquiry / Contact Form Message (if exists) */}
          {contact.message && (
            <Grid item xs={12}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: "8px",
                  p: 2.5,
                  border: "1px solid #e2e8f0",
                  bgcolor: "#ffffff",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.5, pb: 1, borderBottom: "1px solid #f1f5f9" }}>
                  <CommentIcon sx={{ fontSize: 18, color: BRAND_BLUE }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "15px" }}>
                    Enquiry / Form Message
                  </Typography>
                </Box>
                <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                  <Typography variant="body1" sx={{ color: "#1e293b", fontSize: "13.5px", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                    {contact.message}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          )}

          {/* Card: Business Profile & Commercial Operations */}
          {(contact.industryType ||
            contact.businessWebsite ||
            contact.subdomain ||
            contact.yearEstablished ||
            contact.employeeCount ||
            contact.taxId ||
            contact.businessDescription ||
            (contact.productsServicesOffered && contact.productsServicesOffered.length > 0) ||
            (contact.socialMediaLinks && Object.values(contact.socialMediaLinks).some(Boolean))) && (
            <Grid item xs={12}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: "8px",
                  p: 2.5,
                  border: "1px solid #e2e8f0",
                  bgcolor: "#ffffff",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2, pb: 1.2, borderBottom: "1px solid #f1f5f9" }}>
                  <BusinessIcon sx={{ fontSize: 20, color: BRAND_BLUE }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "15px" }}>
                    Business Profile & Online Presence
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {contact.industryType && (
                    <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                        Industry Type
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "14px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {contact.industryType}
                      </Typography>
                    </Grid>
                  )}

                  {contact.yearEstablished && (
                    <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                        Year Established
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "14px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {contact.yearEstablished}
                      </Typography>
                    </Grid>
                  )}

                  {contact.employeeCount !== undefined && contact.employeeCount !== null && (
                    <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                        Employee Count
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "14px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {contact.employeeCount}
                      </Typography>
                    </Grid>
                  )}

                  {contact.taxId && (
                    <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                        Tax ID / GST
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "14px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {contact.taxId}
                      </Typography>
                    </Grid>
                  )}

                  {contact.businessWebsite && (
                    <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                        Business Website
                      </Typography>
                      <MuiLink
                        href={contact.businessWebsite.startsWith("http") ? contact.businessWebsite : `https://${contact.businessWebsite}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ fontWeight: 600, color: BRAND_BLUE, fontSize: "13.5px", display: "inline-flex", alignItems: "center", gap: 0.5, wordBreak: "break-all" }}
                      >
                        {contact.businessWebsite}
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </MuiLink>
                    </Grid>
                  )}

                  {contact.subdomain && (
                    <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                        Subdomain
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: "#0284c7", fontSize: "14px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {contact.subdomain}
                      </Typography>
                    </Grid>
                  )}

                  {contact.socialMediaLinks && Object.keys(contact.socialMediaLinks).length > 0 && (
                    <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                        Social Media Links
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.3 }}>
                        {Object.entries(contact.socialMediaLinks).map(([network, url]) =>
                          url ? (
                            <MuiLink
                              key={network}
                              href={url.startsWith("http") ? url : `https://${url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="none"
                              sx={{
                                fontSize: "12px",
                                fontWeight: 600,
                                px: 1,
                                py: 0.4,
                                borderRadius: "4px",
                                bgcolor: "#f1f5f9",
                                color: BRAND_BLUE,
                                border: "1px solid #cbd5e1",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                wordBreak: "break-all",
                                "&:hover": { bgcolor: "#e2e8f0" },
                              }}
                            >
                              {network}: {url}
                              <OpenInNewIcon sx={{ fontSize: 12 }} />
                            </MuiLink>
                          ) : null
                        )}
                      </Box>
                    </Grid>
                  )}

                  {contact.productsServicesOffered && contact.productsServicesOffered.length > 0 && (
                    <Grid item xs={12} sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.5 }}>
                        Products & Services Offered
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                        {contact.productsServicesOffered.map((product, idx) => (
                          <Chip
                            key={idx}
                            label={product}
                            size="small"
                            variant="outlined"
                            sx={{
                              bgcolor: "#f8fafc",
                              color: "#334155",
                              borderColor: "#cbd5e1",
                              fontWeight: 600,
                              fontSize: "12px",
                            }}
                          />
                        ))}
                      </Box>
                    </Grid>
                  )}

                  {contact.businessDescription && (
                    <Grid item xs={12} sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                        Business Description
                      </Typography>
                      <Box sx={{ p: 1.5, bgcolor: "#f8fafc", borderRadius: "6px", border: "1px solid #e2e8f0", minWidth: 0 }}>
                        <Typography variant="body1" sx={{ color: "#334155", fontSize: "13.5px", lineHeight: 1.5, wordBreak: "break-word", overflowWrap: "anywhere" }}>
                          {contact.businessDescription}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Card>
            </Grid>
          )}

          {/* Card: Dynamic Extensions & Custom Fields */}
          {validExtensionKeys.length > 0 && (
            <Grid item xs={12}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: "8px",
                  p: 2.5,
                  border: "1px solid #e2e8f0",
                  bgcolor: "#ffffff",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2, pb: 1.2, borderBottom: "1px solid #f1f5f9" }}>
                  <ExtensionIcon sx={{ fontSize: 20, color: BRAND_BLUE }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "15px" }}>
                    Extensions & Custom Data
                  </Typography>
                  <Chip
                    label={`${validExtensionKeys.length} ${validExtensionKeys.length === 1 ? "group" : "groups"}`}
                    size="small"
                    sx={{ bgcolor: BRAND_ORANGE_LIGHT, color: BRAND_ORANGE, border: `1px solid ${BRAND_ORANGE_BORDER}`, fontWeight: 700, fontSize: "11.5px", height: 22 }}
                  />
                </Box>

                <Grid container spacing={1.5}>
                  {validExtensionKeys.map((extKey) => {
                    const extData = extensions[extKey];
                    const fieldCount = (typeof extData === "object" && extData !== null)
                      ? (Array.isArray(extData) ? extData.length : Object.keys(extData).length)
                      : 1;

                    if (validExtensionKeys.length > 1) {
                      return (
                        <Grid item xs={12} key={extKey} sx={{ minWidth: 0 }}>
                          <Accordion
                            defaultExpanded={false}
                            disableGutters
                            elevation={0}
                            sx={{
                              border: "1px solid #e2e8f0",
                              borderRadius: "8px !important",
                              bgcolor: "#ffffff",
                              "&:before": { display: "none" },
                              overflow: "hidden",
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon sx={{ fontSize: 20, color: BRAND_BLUE }} />}
                              sx={{
                                minHeight: 48,
                                px: 2,
                                bgcolor: "#f8fafc",
                                "& .MuiAccordionSummary-content": { my: 0.8, display: "flex", alignItems: "center", gap: 1.2 },
                              }}
                            >
                              <Typography sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "14px" }}>
                                {formatLabel(extKey)}
                              </Typography>
                              <Chip
                                label={`${fieldCount} ${Array.isArray(extData) ? "items" : "fields"}`}
                                size="small"
                                sx={{ height: 22, fontSize: "11.5px", bgcolor: "#e2e8f0", color: "#475569", fontWeight: 600 }}
                              />
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 2.5, bgcolor: "#ffffff", borderTop: "1px solid #e2e8f0" }}>
                              {/* Object Extension */}
                              {typeof extData === "object" && extData !== null && !Array.isArray(extData) && (
                                <Grid container spacing={2}>
                                  {Object.keys(extData).map((fieldK) => (
                                    <Grid item xs={12} sm={6} md={4} key={fieldK} sx={{ minWidth: 0 }}>
                                      <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                                        {formatLabel(fieldK)}
                                      </Typography>
                                      {renderValueDisplay(extData[fieldK])}
                                    </Grid>
                                  ))}
                                </Grid>
                              )}

                              {/* Array Extension */}
                              {Array.isArray(extData) && renderValueDisplay(extData)}

                              {/* Primitive Extension */}
                              {typeof extData !== "object" && (
                                <Box sx={{ minWidth: 0 }}>{renderValueDisplay(extData)}</Box>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                      );
                    }

                    return (
                      <Grid item xs={12} key={extKey} sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: "8px",
                            border: "1px solid #e2e8f0",
                            bgcolor: "#ffffff",
                            minWidth: 0,
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "14px", mb: 1.5 }}>
                            {formatLabel(extKey)}
                          </Typography>

                          {/* Object Extension */}
                          {typeof extData === "object" && extData !== null && !Array.isArray(extData) && (
                            <Grid container spacing={2}>
                              {Object.keys(extData).map((fieldK) => (
                                <Grid item xs={12} sm={6} md={4} key={fieldK} sx={{ minWidth: 0 }}>
                                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                                    {formatLabel(fieldK)}
                                  </Typography>
                                  {renderValueDisplay(extData[fieldK])}
                                </Grid>
                              ))}
                            </Grid>
                          )}

                          {/* Array Extension */}
                          {Array.isArray(extData) && renderValueDisplay(extData)}

                          {/* Primitive Extension */}
                          {typeof extData !== "object" && (
                            <Box sx={{ minWidth: 0 }}>{renderValueDisplay(extData)}</Box>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Card>
            </Grid>
          )}

          {/* Card: Dynamic Unmapped Document Metadata (Ensures 100% of collection fields are visible) */}
          {(() => {
            const knownKeys = [
              "_id", "__v", "isDeleted", "password", "otp", "emailOtp", "mobileOtp", "modifiedBy",
              "extensions", "comments", "storeName", "contactName", "email", "phoneNumber", "address",
              "pincode", "region", "location", "image", "businessType", "storeGrowthStage", "subscribedPlan",
              "leadStatus", "industryType", "businessWebsite", "yearEstablished", "employeeCount", "taxId",
              "subdomain", "currency", "productsServicesOffered", "businessDescription", "socialMediaLinks",
              "businessHours", "isWhatsappSubscribed", "whatsappChatStarted", "emailSubscription",
              "whatsappSubscription", "isEmailVerified", "isMobileVerified", "isVerified", "message",
              "onboardingStep", "temporalWorkflowId", "clientConfigId", "stripeCustomerId",
              "stripeSubscriptionId", "stripeSubscriptionStatus", "stripeSubscriptionCurrentPeriodEnd",
              "stripeSubscriptionCancelAtPeriodEnd", "subscriptionCanceledAt", "stripeLastPaymentError",
              "stripeLastPaymentErrorAt", "activeAddOns", "paymentMethod", "planCustomization",
              "createdDate", "updatedDate", "createdAt", "updatedAt", "timezone", "userId"
            ];
            const extraKeys = Object.keys(contact).filter(
              (k) => !knownKeys.includes(k) && contact[k] !== undefined && contact[k] !== null && contact[k] !== ""
            );

            if (extraKeys.length === 0) return null;

            return (
              <Grid item xs={12}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: "8px",
                    p: 2.5,
                    border: "1px solid #e2e8f0",
                    bgcolor: "#ffffff",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2, pb: 1.2, borderBottom: "1px solid #f1f5f9" }}>
                    <DbIcon sx={{ fontSize: 20, color: BRAND_BLUE }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "15px" }}>
                      Additional Document Metadata
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    {extraKeys.map((k) => (
                      <Grid item xs={12} sm={6} md={4} key={k} sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: "#64748b", fontSize: "12px", display: "block", mb: 0.3 }}>
                          {formatLabel(k)}
                        </Typography>
                        {renderValueDisplay(contact[k])}
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              </Grid>
            );
          })()}
        </Grid>
      )}

      {/* TAB 1: EMAIL & WORKFLOW HISTORY */}
      {currentTab === 1 && (
        <Card
          variant="outlined"
          sx={{
            borderRadius: "8px",
            p: 2.5,
            border: "1px solid #e2e8f0",
            bgcolor: "#ffffff",
          }}
        >
          <UserEmailHistory
            email={contact.email}
            userId={contact.userId || contact._id}
            userName={displayName}
          />
        </Card>
      )}

      {/* Messaging / Direct Email Dialog */}
      {messagingOpen && (
        <TemplateSelectionPopup
          open={messagingOpen}
          handleClose={() => setMessagingOpen(false)}
          onClose={() => setMessagingOpen(false)}
          contactId={contact._id || contactId}
          templates={(templates || []).filter((t) => !t.isDefault)}
          type="CRM"
        />
      )}
    </Box>
  );
};

export default CRMDetail;
